import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useToast } from "../ToastContext";

export default function MenuBuilder({ user }) {
  const { addToast } = useToast();
  const [qrs, setQrs] = useState([]);
  const [selectedQR, setSelectedQR] = useState("");
  const [menu, setMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "", description: "", price: "",
    is_veg: true, category_id: "", image_url: ""
  });

  useEffect(() => {
    if (!user?.id) return;
    supabase.from("qr_codes")
      .select("id, name, menu_id")
      .eq("user_id", user.id)
      .then(({ data }) => setQrs(data || []));
  }, [user?.id]);

  useEffect(() => {
    if (!selectedQR) return;
    loadMenu();
  }, [selectedQR]);

  const loadMenu = async () => {
    setLoading(true);
    const qr = qrs.find(q => q.id === selectedQR);
    if (qr?.menu_id) {
      const { data: menuData } = await supabase
        .from("menus").select("*").eq("id", qr.menu_id).single();
      const { data: cats } = await supabase
        .from("menu_categories")
        .select("*, menu_items(*)")
        .eq("menu_id", qr.menu_id)
        .order("sort_order");
      setMenu(menuData);
      setCategories(cats || []);
    } else {
      setMenu(null);
      setCategories([]);
    }
    setLoading(false);
  };

  const createMenu = async () => {
    setSaving(true);
    const qrName = qrs.find(q => q.id === selectedQR)?.name || "Menu";
    const { data: menuData, error } = await supabase
      .from("menus")
      .insert([{ qr_id: selectedQR, user_id: user.id, name: `${qrName} Menu` }])
      .select().single();
    if (!error) {
      await supabase.from("qr_codes").update({ menu_id: menuData.id }).eq("id", selectedQR);
      setMenu(menuData);
      addToast("Menu created!", "success");
    } else {
      addToast("Failed to create menu: " + error.message, "error");
    }
    setSaving(false);
  };

  const addCategory = async () => {
    if (!newCatName.trim() || !menu) return;
    const { data, error } = await supabase
      .from("menu_categories")
      .insert([{ menu_id: menu.id, name: newCatName.trim(), sort_order: categories.length }])
      .select().single();
    if (!error) {
      setCategories(prev => [...prev, { ...data, menu_items: [] }]);
      setNewCatName("");
      addToast("Category added!", "success");
    }
  };

  const handleItemImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `menu-items/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const { error } = await supabase.storage
        .from('menu-images')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });
      if (error) throw error;
      const { data: urlData } = supabase.storage
        .from('menu-images')
        .getPublicUrl(fileName);
      setNewItem(prev => ({ ...prev, image_url: urlData.publicUrl }));
      addToast("Image uploaded!", "success");
    } catch (err) {
      addToast("Image upload failed: " + err.message, "error");
    } finally {
      setUploadingImage(false);
    }
  };

  const addItem = async () => {
    if (!newItem.name || !newItem.category_id) return;
    const { data, error } = await supabase
      .from("menu_items")
      .insert([{
        category_id: newItem.category_id,
        name: newItem.name,
        description: newItem.description || null,
        price: parseFloat(newItem.price) || 0,
        is_veg: newItem.is_veg,
        image_url: newItem.image_url || null,
        is_available: true
      }])
      .select().single();
    if (!error) {
      setCategories(prev => prev.map(c =>
        c.id === newItem.category_id
          ? { ...c, menu_items: [...(c.menu_items || []), data] }
          : c
      ));
      setNewItem({
        name: "", description: "", price: "",
        is_veg: true, category_id: newItem.category_id, image_url: ""
      });
      addToast("Item added!", "success");
    } else {
      addToast("Failed to add item: " + error.message, "error");
    }
  };

  const toggleItem = async (itemId, catId, current) => {
    await supabase.from("menu_items").update({ is_available: !current }).eq("id", itemId);
    setCategories(prev => prev.map(c =>
      c.id === catId
        ? { ...c, menu_items: c.menu_items.map(i => i.id === itemId ? { ...i, is_available: !current } : i) }
        : c
    ));
  };

  const deleteItem = async (itemId, catId) => {
    await supabase.from("menu_items").delete().eq("id", itemId);
    setCategories(prev => prev.map(c =>
      c.id === catId
        ? { ...c, menu_items: c.menu_items.filter(i => i.id !== itemId) }
        : c
    ));
    addToast("Item deleted", "success");
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm("Delete this category and all its items?")) return;
    await supabase.from("menu_categories").delete().eq("id", catId);
    setCategories(prev => prev.filter(c => c.id !== catId));
    addToast("Category deleted", "success");
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-black text-white uppercase italic tracking-tighter">Smart Menu Builder</h1>
        <p className="text-gray-500 text-[10px] uppercase tracking-widest mt-1">Create a digital menu for your QR code</p>
      </div>

      {/* QR Selector */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
        <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest">Select QR Code</p>
        <select value={selectedQR} onChange={e => setSelectedQR(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white outline-none">
          <option value="">Choose a QR code...</option>
          {qrs.map(qr => <option key={qr.id} value={qr.id}>{qr.name || qr.id}</option>)}
        </select>
      </div>

      {selectedQR && loading && (
        <div className="flex justify-center p-10">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {selectedQR && !loading && !menu && (
        <div className="text-center p-10 border border-white/10 rounded-2xl">
          <p className="text-4xl mb-4">🍽️</p>
          <p className="text-white font-black mb-2">No menu yet</p>
          <p className="text-gray-500 text-sm mb-6">Create a digital menu for this QR code</p>
          <button onClick={createMenu} disabled={saving}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-xl text-[10px] font-black uppercase text-white transition-all">
            {saving ? "Creating..." : "Create Menu"}
          </button>
        </div>
      )}

      {menu && (
        <div className="space-y-6">

          {/* Add Category */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest">Add Category</p>
            <div className="flex gap-3">
              <input
                value={newCatName}
                onChange={e => setNewCatName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addCategory()}
                placeholder="e.g. Starters, Main Course, Desserts"
                className="flex-1 bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-blue-500"
              />
              <button onClick={addCategory}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-[10px] font-black uppercase text-white">
                Add
              </button>
            </div>
          </div>

          {/* Add Item */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest">Add Menu Item</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select
                value={newItem.category_id}
                onChange={e => setNewItem({ ...newItem, category_id: e.target.value })}
                className="bg-white/5 border border-white/10 rounded-xl p-3 text-white outline-none"
              >
                <option value="">Select category...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>

              <input
                value={newItem.name}
                onChange={e => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="Item name *"
                className="bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-blue-500"
              />

              <input
                value={newItem.description}
                onChange={e => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Description (optional)"
                className="bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-blue-500"
              />

              <input
                type="number"
                value={newItem.price}
                onChange={e => setNewItem({ ...newItem, price: e.target.value })}
                placeholder="Price (₹)"
                className="bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-blue-500"
              />

              {/* Image Upload — full width */}
              <div className="md:col-span-2">
                <p className="text-[9px] font-black uppercase text-gray-500 mb-2">Item Photo (optional)</p>
                <div className="flex items-center gap-3">
                  {newItem.image_url && (
                    <img
                      src={newItem.image_url}
                      alt="preview"
                      className="w-16 h-16 rounded-xl object-cover border border-white/10 shrink-0"
                    />
                  )}
                  <label className="flex-1 border-2 border-dashed border-white/10 rounded-xl p-4 text-center cursor-pointer hover:border-blue-500/50 transition-all relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleItemImageUpload}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      disabled={uploadingImage}
                    />
                    <p className="text-gray-500 text-[10px] font-black uppercase">
                      {uploadingImage ? "⏳ Uploading..." : newItem.image_url ? "📷 Change Photo" : "📷 Upload Photo"}
                    </p>
                    <p className="text-gray-700 text-[9px] mt-1">JPG, PNG, WEBP supported</p>
                  </label>
                  {newItem.image_url && (
                    <button
                      onClick={() => setNewItem({ ...newItem, image_url: "" })}
                      className="text-red-500 text-[10px] font-black uppercase px-3 py-2 bg-red-500/10 rounded-xl hover:bg-red-500/20 shrink-0"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setNewItem({ ...newItem, is_veg: !newItem.is_veg })}
                  className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center text-lg transition-all ${newItem.is_veg
                    ? "border-green-500 bg-green-500/20"
                    : "border-red-500 bg-red-500/20"
                  }`}
                >
                  {newItem.is_veg ? "🟢" : "🔴"}
                </button>
                <div>
                  <p className={`text-[10px] font-black uppercase ${newItem.is_veg ? "text-green-400" : "text-red-400"}`}>
                    {newItem.is_veg ? "Vegetarian" : "Non-Vegetarian"}
                  </p>
                  <p className="text-[9px] text-gray-600">Tap to toggle</p>
                </div>
              </div>
              <button
                onClick={addItem}
                disabled={!newItem.name || !newItem.category_id || uploadingImage}
                className="px-6 py-3 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-[10px] font-black uppercase text-white transition-all"
              >
                Add Item
              </button>
            </div>
          </div>

          {/* Categories & Items */}
          {categories.length === 0 ? (
            <div className="text-center p-10 border border-white/10 rounded-2xl">
              <p className="text-gray-500 text-sm">No categories yet. Add one above.</p>
            </div>
          ) : categories.map(cat => (
            <div key={cat.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <div>
                  <h3 className="text-white font-black uppercase tracking-widest text-sm">{cat.name}</h3>
                  <p className="text-gray-500 text-[9px]">{(cat.menu_items || []).length} items</p>
                </div>
                <button
                  onClick={() => deleteCategory(cat.id)}
                  className="text-red-500 text-[10px] font-black uppercase hover:text-red-400 transition-colors"
                >
                  Delete Category
                </button>
              </div>

              {(!cat.menu_items || cat.menu_items.length === 0) ? (
                <p className="px-6 py-4 text-gray-500 text-sm">No items yet.</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {cat.menu_items.map(item => (
                    <div key={item.id} className={`flex items-center gap-4 px-6 py-4 ${!item.is_available ? "opacity-40" : ""}`}>

                      {/* Item image */}
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-14 h-14 rounded-xl object-cover border border-white/10 shrink-0"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                          <span className="text-2xl">{item.is_veg ? "🟢" : "🔴"}</span>
                        </div>
                      )}

                      {/* Item details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {item.image_url && <span className="text-sm">{item.is_veg ? "🟢" : "🔴"}</span>}
                          <p className="text-white text-sm font-black truncate">{item.name}</p>
                        </div>
                        {item.description && (
                          <p className="text-gray-500 text-[10px] mt-0.5 truncate">{item.description}</p>
                        )}
                        <p className="text-white font-black text-sm mt-1">₹{item.price}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => toggleItem(item.id, cat.id, item.is_available)}
                          className={`text-[9px] font-black uppercase px-2 py-1.5 rounded-lg transition-all ${item.is_available
                            ? "bg-green-500/20 text-green-400 hover:bg-red-500/20 hover:text-red-400"
                            : "bg-red-500/20 text-red-400 hover:bg-green-500/20 hover:text-green-400"
                          }`}
                        >
                          {item.is_available ? "Available" : "Unavailable"}
                        </button>
                        <button
                          onClick={() => deleteItem(item.id, cat.id)}
                          className="text-red-500 hover:text-red-400 text-[10px] font-black transition-colors px-2 py-1.5"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}